import { EVENT_LABELS, EventType } from "../eventTypes";
import { formatCount, formatDuration, formatPeriod, Granularity } from "../reportFormat";
import { DayWindow, formatDays } from "../scorecardFormat";
import { CellSpec, PdfDoc } from "./pdfDoc";
import { heading, HOUSE_WIDTH, HouseOptions, houseDoc, houseTable, note, paragraph, table, titleBlock, withMeaning } from "./houseStyle";

/** The Engagement tab as a PDF: active, new and returning members, time in the app, what they did, per period. */

export interface EngagementTotals {
  activeMembers: number;
  newMembers: number;
  returningMembers: number;
  sessions: number;
  engagementSeconds: number;
  averageSessionSeconds: number;
  averageEngagementSecondsPerMember: number;
  averageSessionsPerMember: number;
  screenViews: number;
}

export interface EngagementPeriod {
  period: string;
  activeMembers: number;
  newMembers: number;
  returningMembers: number;
  sessions: number;
  engagementSeconds: number;
}

export interface EngagementPdfData {
  range: DayWindow;
  timezone: string;
  region: string | null;
  granularity: Granularity;
  totals: EngagementTotals;
  events: Array<{ type: string; count: number }>;
  periods: EngagementPeriod[];
}

const EVENT_MEANINGS: Record<EventType, string> = {
  REGISTRATION: "Accounts registered.",
  APP_OPEN: "Sign-ins and app opens with a saved session.",
  INVITE_SHARED: "Times a member shared their invitation link.",
  REFERRAL: "Members who registered through someone's link.",
  RECOMMENDATION: "Places recommended.",
  LEVEL_CHANGE: "Times a member's level changed; recorded from Oct 20, 2026.",
  TEAM_JOINED: "Communities joined.",
  TEAM_MESSAGE: "Messages the app logged in communities.",
};

export function buildEngagementPdf(data: EngagementPdfData, options: HouseOptions): PdfDoc {
  const { totals } = data;
  const of = (part: number) => (totals.activeMembers ? ` (${Math.round((part / totals.activeMembers) * 100)}% of active)` : "");
  const kpi = (label: string, value: string, meaning: string) =>
    withMeaning([{ text: label, style: { style: "bold" } }, { text: value, align: "right" }], options.includeMeanings ? meaning : null);
  const kpiTable = (rows: CellSpec[][][]) => table(houseTable([HOUSE_WIDTH - 170, 170], [{ text: "Number" }, { text: "In these dates", align: "right" }], rows.flat()));

  const blocks = titleBlock(
    "Engagement",
    [formatDays(data.range), `Region: ${data.region ?? "Everywhere"} · Days in ${data.timezone}`],
    options.preparedBy,
  );
  if (options.includeMeanings) {
    blocks.push(
      paragraph(
        "How many members used the app in these dates, how long for, and what they did. Totals count each member once over the whole range, so they are usually less than the periods added up.",
        12,
      ),
    );
  }

  blocks.push(heading("Members"));
  blocks.push(
    kpiTable([
      kpi("Active members", formatCount(totals.activeMembers), "Opened the app or used it in these dates."),
      kpi("New members", `${formatCount(totals.newMembers)}${of(totals.newMembers)}`, "Active members who registered in these dates."),
      kpi(
        "Returning members",
        `${formatCount(totals.returningMembers)}${of(totals.returningMembers)}`,
        "Active members who registered before these dates began.",
      ),
    ]),
  );

  blocks.push(heading("Time in the app"));
  blocks.push(
    kpiTable([
      kpi("Sessions", `${formatCount(totals.sessions)} (${totals.averageSessionsPerMember.toFixed(1)} per active member)`, "A run of activity with no gap longer than 30 minutes."),
      kpi("Average session", formatDuration(totals.averageSessionSeconds), "From the first to the last screen of a session. A minimum: the app records when a screen opens, not when it closes."),
      kpi("Time per active member", formatDuration(totals.averageEngagementSecondsPerMember), "Time in the app divided by active members."),
      kpi("Total time", formatDuration(totals.engagementSeconds), "Every session's time added up."),
      kpi("Screen views", formatCount(totals.screenViews), "Screens opened, each counted once."),
    ]),
  );

  const counts = new Map(data.events.map((event) => [event.type, event.count]));
  blocks.push(heading("What members did"));
  blocks.push(
    kpiTable(
      (Object.keys(EVENT_LABELS) as EventType[]).map((type) =>
        kpi(EVENT_LABELS[type].plural, formatCount(counts.get(type)), EVENT_MEANINGS[type]),
      ),
    ),
  );

  blocks.push(heading("New and returning members, period by period"));
  blocks.push(note("Most recent first. A member active in two periods counts in both."));
  const width = (HOUSE_WIDTH - 120) / 5;
  blocks.push(
    table(
      houseTable(
        [120, width, width, width, width, width],
        [
          { text: "Period" },
          { text: "Active", align: "right" },
          { text: "New", align: "right" },
          { text: "Returning", align: "right" },
          { text: "Sessions", align: "right" },
          { text: "Time in app", align: "right" },
        ],
        [...data.periods].reverse().map((period) => [
          { text: formatPeriod(period.period, data.granularity) },
          { text: formatCount(period.activeMembers), align: "right" },
          { text: formatCount(period.newMembers), align: "right" },
          { text: formatCount(period.returningMembers), align: "right" },
          { text: formatCount(period.sessions), align: "right" },
          { text: formatDuration(period.engagementSeconds), align: "right" },
        ]),
      ),
    ),
  );

  return houseDoc(`ILBP Engagement ${data.range.from} to ${data.range.to}.pdf`, blocks, "ILBP Engagement");
}
